import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DEPARTMENTS, POSITIONS_BY_DEPARTMENT } from "./departments";
import {
  createAnnouncement,
  getAllAnnouncements,
  updateAnnouncement,
  deleteAnnouncement,
  getApplications,
} from "../api/api";
import { extractCollection } from "../utils/extractCollection";
import "./CareersAdmin.css";

// ── constants ─────────────────────────────────────────────────────────────────
const JOB_TYPES = ["Full-Time", "Part-Time", "Remote", "Internship", "Hybrid"];

// ── empty form state ──────────────────────────────────────────────────────────
const EMPTY_FORM = {
  title: "",
  department: "",
  position: "",
  location: "",
  type: "",
  publish: true,
  description: "",
};

const JOB_TITLES = [
  "HR", "Art Director", "Media Buyer", "Content Creator", "Graphic Designer",
  "Moderator", "Social Media Specialist", "UI/UX Designer", "Frontend Developer",
  "Backend Developer", "Media Production", "Accountant", "Legal Affairs",
  "Secretary", "Office Boy", "Other", "Ads Advertising", "SEO Specialist"
];

// ── JobForm (create / edit) ───────────────────────────────────────────────────
function JobForm({ initial = EMPTY_FORM, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});

  const availablePositions = POSITIONS_BY_DEPARTMENT[form.department] || [];
  const showPosition = form.department && availablePositions.length > 0;

  const set = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    setErrors((p) => { const n = { ...p }; delete n[field]; return n; });
  };

  const handleDept = (e) => {
    setForm((p) => ({ ...p, department: e.target.value, position: "" }));
    setErrors((p) => { const n = { ...p }; delete n.department; return n; });
  };

  const validate = () => {
    const e = {};
    if (typeof form.title !== 'string' || !form.title.trim()) e.title = "Title is required";
    if (typeof form.department !== 'string' || !form.department.trim()) e.department = "Department is required";
    if (showPosition && (!form.position || !form.position.trim())) e.position = "Position is required";
    if (typeof form.location !== 'string' || !form.location.trim()) e.location = "Location is required";
    if (typeof form.type !== 'string' || !form.type.trim()) e.type = "Type is required";
    if (typeof form.description !== 'string' || !form.description.trim()) e.description = "Description is required";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    // ✅ publish يتبعت كـ boolean
    onSave({ ...form, publish: Boolean(form.publish) });
  };

  return (
    <div className="ca-form-card">
      <div className="ca-form-grid">

        {/* Title */}
        <div className="ca-field ca-field--full">
          <label className="ca-label">Job Title <span className="ca-req">*</span></label>
          <select
            className={`ca-input${errors.title ? " ca-input--err" : ""}`}
            value={form.title}
            onChange={set("title")}
          >
            <option value="">Select Title</option>
            {JOB_TITLES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          {errors.title && <span className="ca-err-msg">{errors.title}</span>}
        </div>

        {/* Department */}
        <div className="ca-field">
          <label className="ca-label">Department <span className="ca-req">*</span></label>
          <select
            className={`ca-input${errors.department ? " ca-input--err" : ""}`}
            value={form.department}
            onChange={handleDept}
          >
            <option value="">Select Department</option>
            {DEPARTMENTS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          {errors.department && <span className="ca-err-msg">{errors.department}</span>}
        </div>

        {/* Position — only when department has positions */}
        {showPosition ? (
          <div className="ca-field">
            <label className="ca-label">Position</label>
            <select className="ca-input" value={form.position} onChange={set("position")}>
              <option value="">Select Position</option>
              {availablePositions.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="ca-field" />
        )}

        {/* ✅ Location — REQUIRED */}
        <div className="ca-field">
          <label className="ca-label">Location <span className="ca-req">*</span></label>
          <input
            className={`ca-input${errors.location ? " ca-input--err" : ""}`}
            placeholder="e.g. Nasr City, Cairo"
            value={form.location}
            onChange={set("location")}
          />
          {errors.location && <span className="ca-err-msg">{errors.location}</span>}
        </div>

        {/* ✅ Type — REQUIRED */}
        <div className="ca-field">
          <label className="ca-label">Type <span className="ca-req">*</span></label>
          <select
            className={`ca-input${errors.type ? " ca-input--err" : ""}`}
            value={form.type}
            onChange={set("type")}
          >
            <option value="">Select Type</option>
            {JOB_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          {errors.type && <span className="ca-err-msg">{errors.type}</span>}
        </div>

        {/* ✅ Publish — REQUIRED boolean */}
        <div className="ca-field">
          <label className="ca-label">Publish</label>
          <select
            className="ca-input"
            value={form.publish ? "true" : "false"}
            onChange={(e) => setForm((p) => ({ ...p, publish: e.target.value === "true" }))}
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        </div>

        {/* Description */}
        <div className="ca-field ca-field--full">
          <label className="ca-label">Description <span className="ca-req">*</span> <span className="ca-hint">(max 1024 chars)</span></label>
          <textarea
            className={`ca-input ca-textarea${errors.description ? " ca-input--err" : ""}`}
            placeholder="Describe the role, responsibilities, requirements..."
            value={form.description}
            onChange={set("description")}
            maxLength={1024}
          />
          <span className="ca-char-count">{form.description.length} / 1024</span>
          {errors.description && <span className="ca-err-msg">{errors.description}</span>}
        </div>

      </div>

      {/* Actions */}
      <div className="ca-form-actions">
        {onCancel && (
          <button className="ca-btn ca-btn--ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
        <button className="ca-btn ca-btn--gold" onClick={handleSubmit} disabled={saving}>
          {saving
            ? <><span className="ca-spinner" /> Saving...</>
            : initial === EMPTY_FORM ? "Post Job →" : "Save Changes →"}
        </button>
      </div>
    </div>
  );
}

// ── JobRow ────────────────────────────────────────────────────────────────────
function JobRow({ job, onEdit, onDelete, deleting }) {
  return (
    <div className="ca-job-row">
      <div className="ca-job-main">
        <div className="ca-job-badges">
          {job.department && <span className="ca-badge ca-badge--dept">{job.department}</span>}
          {job.position && <span className="ca-badge ca-badge--pos">{job.position}</span>}
          {job.type && <span className="ca-badge ca-badge--type">{job.type}</span>}
          {job.location && <span className="ca-badge ca-badge--loc">📍 {job.location}</span>}
          <span className={`ca-badge ${job.publish ? "ca-badge--pub" : "ca-badge--draft"}`}>
            {job.publish ? "Published" : "Draft"}
          </span>
        </div>
        <div className="ca-job-title">{job.title}</div>
        {job.description && (
          <div className="ca-job-desc">{job.description}</div>
        )}
      </div>
      <div className="ca-job-actions">
        <button className="ca-icon-btn ca-icon-btn--edit" onClick={() => onEdit(job)} title="Edit">
          ✎
        </button>
        <button
          className="ca-icon-btn ca-icon-btn--del"
          onClick={() => onDelete(job._id || job.id)}
          disabled={deleting}
          title="Delete"
        >
          {deleting ? "…" : "✕"}
        </button>
      </div>
    </div>
  );
}

// ── EditModal ─────────────────────────────────────────────────────────────────
function EditModal({ job, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [apiErr, setApiErr] = useState("");

  const handleSave = async (form) => {
    setSaving(true);
    setApiErr("");
    try {
      await updateAnnouncement(job._id || job.id, form);
      onSaved();
      onClose();
    } catch (err) {
      setApiErr(err?.data?.message || "Failed to update. Try again.");
    }
    finally {
      setSaving(false);
    }
  };

  return (
    <div className="ca-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ca-modal">
        <div className="ca-modal-header">
          <h2 className="ca-modal-title">Edit Job</h2>
          <button className="ca-modal-close" onClick={onClose}>×</button>
        </div>
        {apiErr && <div className="ca-api-err">{apiErr}</div>}
        <JobForm
          initial={{
            title: job.title || "",
            department: job.department || "",
            position: job.position || "",
            location: job.location || "",
            type: job.type || "",
            publish: job.publish ?? true,
            description: job.description || "",
          }}
          onSave={handleSave}
          onCancel={onClose}
          saving={saving}
        />
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
// ── Main Page ─────────────────────────────────────────────────────────────────
function getApplicantName(application) {
  return application?.fullName || application?.name || "Unknown";
}

function getApplicantRole(application) {
  return (
    application?.position ||
    application?.jobTitle ||
    application?.title ||
    application?.department ||
    "—"
  );
}

export default function CareersAdmin() {
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appsLoading, setAppsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editJob, setEditJob] = useState(null);
  const [apiErr, setApiErr] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [formKey, setFormKey] = useState(0);

  const fetchJobs = async () => {
    try {
      const data = await getAllAnnouncements();
      setJobs(extractCollection(data));
    } catch (err) {
      setJobs([]);
      setApiErr(err?.data?.message || err?.message || "Failed to load job openings.");
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    setAppsLoading(true);
    try {
      const data = await getApplications({ page: 1, limit: 50 });
      setApplications(extractCollection(data));
    } catch {
      setApplications([]);
    } finally {
      setAppsLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchApplications();
  }, []);

  const flash = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const handleCreate = async (form) => {
    setSaving(true);
    setApiErr("");
    try {
      await createAnnouncement(form);
      await fetchJobs();
      setFormKey((k) => k + 1); // ✅ ده بيعمل reset للـ form
      flash("Job posted successfully! ✅");
    } catch (err) {
      setApiErr(err?.data?.message || "Failed to post job. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this job posting?")) return;
    setDeletingId(id);
    try {
      await deleteAnnouncement(id);
      setJobs((p) => p.filter((j) => (j._id || j.id) !== id));
      flash("Job deleted.");
    } catch {
      setApiErr("Failed to delete. Try again.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="ca-page">

      {/* ── Header ── */}
      <div className="ca-header">
        <div>
          <div className="ca-header-tag">Admin · Careers</div>
          <h1 className="ca-header-title" style={{ fontFamily: 'Roboto, sans-serif' }}>Job Postings</h1>
        </div>
        <div className="ca-header-count">
          {jobs.length} {jobs.length === 1 ? "position" : "positions"} open
          {" · "}
          {applications.length} {applications.length === 1 ? "application" : "applications"}
        </div>
      </div>

      {/* ── Global messages ── */}
      {apiErr && <div className="ca-banner ca-banner--err">{apiErr}</div>}
      {successMsg && <div className="ca-banner ca-banner--ok">{successMsg}</div>}

      {/* ── Create form ── */}
      <section className="ca-section">
        <div className="ca-section-label">Post a New Job</div>
        <JobForm key={formKey} onSave={handleCreate} saving={saving} />
      </section>

      {/* ── Job list ── */}
      <section className="ca-section">
        <div className="ca-section-label">Current Openings</div>

        {loading ? (
          <div className="ca-empty">Loading...</div>
        ) : jobs.length === 0 ? (
          <div className="ca-empty">No job postings yet.</div>
        ) : (
          <div className="ca-job-list">
            {jobs.map((job) => (
              <JobRow
                key={job._id || job.id}
                job={job}
                onEdit={setEditJob}
                onDelete={handleDelete}
                deleting={deletingId === (job._id || job.id)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="ca-section">
        <div className="ca-section-label-row">
          <div className="ca-section-label">Website Applications</div>
          <Link className="ca-section-link" to="/careers-applications">
            View all →
          </Link>
        </div>

        {appsLoading ? (
          <div className="ca-empty">Loading applications...</div>
        ) : applications.length === 0 ? (
          <div className="ca-empty">
            No applications yet. Submissions from the website careers page will appear here.
          </div>
        ) : (
          <div className="ca-job-list">
            {applications.slice(0, 8).map((application) => (
              <div className="ca-job-row" key={application._id || application.id}>
                <div className="ca-job-main">
                  <div className="ca-job-badges">
                    <span className="ca-badge ca-badge--dept">{getApplicantRole(application)}</span>
                    {application.department && (
                      <span className="ca-badge ca-badge--pos">{application.department}</span>
                    )}
                  </div>
                  <div className="ca-job-title">{getApplicantName(application)}</div>
                  <div className="ca-job-desc">
                    {application.email || "No email"}
                    {application.message ? ` · ${application.message}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Edit modal ── */}
      {editJob && (
        <EditModal
          job={editJob}
          onClose={() => setEditJob(null)}
          onSaved={fetchJobs}
        />
      )}

    </div>
  );
}